import { Controller } from '@hotwired/stimulus'

// Client-side search + filter for the AI Models settings page.
//
// Targets:
//   - search:       <input type="search">
//   - providerPill: <span> badge per provider (data-provider="OpenAI" / "")
//   - viewToggle:   <button> per view (data-view="all" / "enabled" / "disabled")
//   - section:      <section> per provider — hidden when no card inside matches
//   - emptyState:   shown when no card matches across all sections
//
// Cards live anywhere in the DOM with data-ai-models-filter-target="card" plus
// data-provider / data-enabled / data-model-id / data-model-name attributes.
export default class extends Controller {
  static targets = ['search', 'providerPill', 'viewToggle', 'section', 'emptyState', 'card']

  connect() {
    this.providerFilter = ''
    this.viewFilter     = 'all'
    this.searchQuery    = ''
    this.applyFilters()
  }

  // Re-apply current filters whenever a new card or section is streamed in
  // (e.g. after a toggle or sync turbo_stream replacement).
  cardTargetConnected() {
    this.applyFilters()
  }

  sectionTargetConnected() {
    this.applyFilters()
  }

  selectProvider(event) {
    const pill = event.currentTarget
    this.providerFilter = pill.dataset.provider || ''
    this.providerPillTargets.forEach((p) => {
      p.dataset.active = (p === pill).toString()
    })
    this.applyFilters()
  }

  selectView(event) {
    const btn = event.currentTarget
    this.viewFilter = btn.dataset.view || 'all'
    this.viewToggleTargets.forEach((b) => {
      const isActive = b === btn
      b.classList.toggle('btn-dark', isActive)
      b.classList.toggle('btn-outline-dark', !isActive)
    })
    this.applyFilters()
  }

  search(event) {
    this.searchQuery = (event.target.value || '').trim().toLowerCase()
    this.applyFilters()
  }

  applyFilters() {
    let visibleTotal = 0

    this.cardTargets.forEach((card) => {
      const matchesProvider = !this.providerFilter || card.dataset.provider === this.providerFilter
      const matchesView     = this._matchesView(card)
      const matchesSearch   = this._matchesSearch(card)
      const visible         = matchesProvider && matchesView && matchesSearch

      card.dataset.hidden = (!visible).toString()
      if (visible) visibleTotal += 1
    })

    // Hide sections that have no visible cards
    this.sectionTargets.forEach((section) => {
      const visibleCards = section.querySelectorAll('[data-ai-models-filter-target~="card"][data-hidden="false"]')
      section.dataset.hidden = (visibleCards.length === 0).toString()
    })

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle('d-none', visibleTotal > 0)
    }
  }

  _matchesView(card) {
    if (this.viewFilter === 'enabled')  return card.dataset.enabled === 'true'
    if (this.viewFilter === 'disabled') return card.dataset.enabled === 'false'
    return true
  }

  _matchesSearch(card) {
    if (!this.searchQuery) return true
    const haystack = `${card.dataset.modelName || ''} ${card.dataset.modelId || ''}`.toLowerCase()
    return haystack.includes(this.searchQuery)
  }
}
